const resumePF = `
    _   __              ______                                   __ 
   / | / /___  ___     / ____/_  _____  ____  ____ _____ _____  / /_
  /  |/ / __ \\/ _ \\   / / __/ / / / _ \\/ __ \\/ __ \`/ __ \`/ __ \\/ __/
 / /|  / /_/ /  __/  / /_/ / /_/ /  __/ / / / /_/ / /_/ / / / / /_  
/_/ |_/\\____/\\___/   \\____/\\__,_/\\___/_/ /_/\\__, /\\__,_/_/ /_/\\__/  
                                           /____/                   
====================================================================
Noé Guengant | 21 | AI & Data Science Engineering Student
Angers, France | guengant.noe@gmail.com | Github: @NhkI0
====================================================================

===============
ABOUT ME
===============
I'm an AI/Data Sciences engineer from France who believes in building 
models that help real people do real things. The inspiration behind my 
work and aesthetic comes from my parents’ old, whirring computer—the 
one that introduced me to the internet and shaped my curiosity. 

In a world full of superficial "AI vibe coding" and boring copy-pasted 
portfolios, I prefer to stand out. I lean into a slightly outcast, 
retro aesthetic because I want to be remembered. Beyond the visuals, 
my core focus is on in-depth AI specialization, transforming complex 
papers into functional prototypes, and delivering end-to-end solutions 
that matter. 

Currently looking for: An internship or first role around recommendation 
systems, data analysis, or AI consulting. I am ideally looking for 
opportunities in the Netherlands, but I'm not picky if the project is 
exciting and technically rigorous.

===============
ACADEMIC BACKGROUND & INTERNATIONAL MOBILITY
===============
2024 - Present: ESAIP (St Barthélemy d'Anjou, France)
Engineering diploma, specialization in AI & Data Analysis. 
- Machine Learning, Optimization Algorithms, Image Analysis, Data 
  Engineering, Probabilities, and Ethics of AI.

2026: Universidad Bernardo O'Higgins (Santiago, Chile) - Erasmus
- Upcoming 6-month academic exchange.

2025: VŠB-TU Ostrava (Czechia) - Erasmus
- x86 Assembly and Image Analysis. Long-form projects from start to 
  finish; the most technically challenging and useful semester so far.

2024: VILNIUS TECH (Lithuania) - Erasmus
- Java, Statistics, and everyday English immersion. Began learning 
  Dutch during this period.

2023: Riga Technical University (Latvia) - Summer School
- 5G networks applied to autonomous drones.

2022 - 2024: ESAIP Preparatory Classes (France)
- Two intense years of math, physics, CS, and environmental studies. 
  100% English lectures in a rare mixed informatics/environment program.

===============
WORK EXPERIENCE
===============
Summer 2026: ESAIP -- Research Internship
- Transformed natural biochemical water processes into empirical and 
  AI-driven models. The primary goal was to develop an open-source 
  software tool to simulate water treatment and assist researchers.

Summer 2025: Baker Tilly France -- Data Architect Intern
- Built a comprehensive logging system for an internal data pipeline. 
  Subjected to rigorous code reviews from a highly demanding tutor, 
  which taught me industry-standard practices and what "professional 
  Python" actually means.

2023 - 2026: Volunteer Informatics Teacher
- Taught computer basics to elders at a local association. Featured in 
  Ouest-France. Developed considerable patience and the ability to 
  explain complex technical concepts simply.

2023 - 2024: McDonald's -- Crewman
- Paid the bills and learned teamwork under genuine pressure. Confirmed 
  my drive to become the best in the AI/Data field so I never have to 
  work on an assembly line again.

===============
PROJECTS & COMPETITIONS
===============
D4G - Green AI Competition (Inter-school)
- Tasked with summarizing text in 10-15 words while consuming the least 
  energy possible. Forced the team to treat model size and memory as 
  strict engineering constraints rather than footnotes.

Cryptocurrency Value Prediction
- Built an AI-driven trading agent utilizing a multi-agent strategy 
  in Python.

Discord Livechat
- Developed a custom server/client overlay for real-time media 
  broadcasting because the official streaming tools weren't enough.

SpotifyDock
- Custom desktop integration for tailored environment control.

===============
TECHNICAL SKILLS
===============
AI & Data Science:
- Python, Machine Learning, Data Analysis, Deep Learning
- Pandas, NumPy, Scikit-learn, TensorFlow/PyTorch basics
- Data Engineering, Image Analysis, Optimization Algorithms

Databases & Backend:
- SQL, PostgreSQL, JPQL

Languages & Frameworks:
- React, JavaScript, C/C++, Java, HTML/CSS
- x86 Assembly

Infrastructure & Tools:
- Git, Linux/Unix

===============
LANGUAGES & ACHIEVEMENTS
===============
- English: Fluent (TOEIC: 990/990)
- French: Native
- Dutch: Actively learning (still confused about 'de' vs 'het')
- Highly adaptable and open-minded, shaped by 4 Erasmus exchanges and 
  international collaboration.
`;

export default resumePF;
